import { Box, Button, IconButton, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import ProfitRiskBox from "components/common/ProfitRiskBox";
import ScoreBox from "components/common/ScoreBox";

const PRIZES = { 1: 500, 2: 300, 3: 200 };
const POSITION_STYLES = {
  1: {
    color: "#5D4300",
    accent: "#B98200",
    background:
      "linear-gradient(135deg, #FFF8DC 0%, #FFE49A 52%, #F5C64B 100%)",
    border: "rgba(174, 121, 0, 0.32)",
  },
  2: {
    color: "#404A59",
    accent: "#6C7787",
    background: "linear-gradient(145deg, #F8FAFC 0%, #DDE3EA 100%)",
    border: "rgba(91, 104, 120, 0.22)",
  },
  3: {
    color: "#6F3B1E",
    accent: "#A85F32",
    background: "linear-gradient(145deg, #FFF5ED 0%, #E9BB99 100%)",
    border: "rgba(137, 76, 38, 0.24)",
  },
};

const getName = (item) =>
  item?.title || item?.robotName || item?.strategyName || "-";

const metricNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const DashboardCompetitionCard = ({
  title,
  items,
  lastUpdated,
  onOpenMenu,
  infoUrl,
  joinUrl,
}) => {
  const { t, i18n } = useTranslation("dashboard");
  const locale = i18n.language === "es" ? "es-ES" : "en-GB";
  const orderedItems = [...items]
    .sort((left, right) => Number(left.position) - Number(right.position))
    .slice(0, 3);
  const first = orderedItems[0];
  const remaining = orderedItems.slice(1);

  return (
    <Box
      sx={{
        flex: { lg: "0 1 32%", xs: "1 1 auto" },
        minWidth: { lg: 290, xs: 0 },
        display: "flex",
      }}
    >
      <Box
        sx={{
          width: "100%",
          p: 1.4,
          borderRadius: "10px",
          background: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          border: "1px solid #E6EAF0",
          overflow: "hidden",
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={0.8}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              color: "#8A5B00",
              background: "#FFF1BE",
              flexShrink: 0,
            }}
          >
            <EmojiEventsIcon fontSize="small" />
          </Box>
          <Box minWidth={0} flex={1}>
            <Typography color="#253E66" fontWeight={850} fontSize={14.5} noWrap>
              {title}
            </Typography>
            {lastUpdated && (
              <Typography variant="caption" color="#8A93A2" noWrap>
                {t("competition.updated", "Actualizado")}:{" "}
                {new Intl.DateTimeFormat(locale, {
                  day: "2-digit",
                  month: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                }).format(lastUpdated)}
              </Typography>
            )}
          </Box>
          <Button
            component={RouterLink}
            to="/tournament"
            size="small"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              flexShrink: 0,
              minWidth: 0,
              borderRadius: "8px",
              textTransform: "none",
              fontSize: 10.5,
              fontWeight: 800,
              px: 1.1,
              py: 0.45,
              "& .MuiButton-endIcon": { ml: 0.55 },
              "& .MuiSvgIcon-root": { fontSize: 17 },
            }}
          >
            {t("viewAll", "Ver todo")}
          </Button>
        </Box>

        <Box display="flex" gap={0.7} mb={0.9}>
          <Button
            href={joinUrl}
            target="_blank"
            rel="noreferrer"
            size="small"
            variant="contained"
            sx={{
              flex: 1,
              minWidth: 0,
              textTransform: "none",
              fontSize: 10.5,
              fontWeight: 750,
              py: 0.35,
            }}
          >
            {t("competition.participate", "Participar")}
          </Button>
          <Button
            href={infoUrl}
            target="_blank"
            rel="noreferrer"
            size="small"
            variant="outlined"
            sx={{
              flex: 1,
              minWidth: 0,
              textTransform: "none",
              fontSize: 10.5,
              fontWeight: 750,
              py: 0.35,
            }}
          >
            {t("competition.howItWorks", "Como funciona")}
          </Button>
        </Box>

        {first ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "grid",
              gridTemplateRows:
                remaining.length > 0
                  ? "minmax(154px, 1.35fr) minmax(118px, 0.9fr)"
                  : "1fr",
              gap: 0.8,
            }}
          >
            <Box
              sx={{
                position: "relative",
                p: 1.35,
                borderRadius: "13px",
                color: POSITION_STYLES[1].color,
                background: POSITION_STYLES[1].background,
                border: `1px solid ${POSITION_STYLES[1].border}`,
                boxShadow: "0 10px 22px rgba(197, 148, 25, 0.18)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: 120,
                  height: 120,
                  right: -50,
                  top: -58,
                  borderRadius: "50%",
                  border: "20px solid rgba(255,255,255,0.28)",
                },
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                position="relative"
                zIndex={1}
              >
                <Box display="flex" alignItems="center" gap={0.8}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "12px",
                      display: "grid",
                      placeItems: "center",
                      color: "#FFFFFF",
                      background: "#B98200",
                      boxShadow: "0 6px 12px rgba(123, 86, 0, 0.22)",
                    }}
                  >
                    <EmojiEventsIcon />
                  </Box>
                  <Box>
                    <Typography
                      fontSize={10}
                      fontWeight={900}
                      letterSpacing={0.7}
                    >
                      {t("competition.firstPlace", "PRIMER PUESTO")}
                    </Typography>
                    <Typography fontSize={18} fontWeight={950} lineHeight={1}>
                      {PRIZES[1]} EUR
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={(event) => onOpenMenu(event, first)}
                  aria-label={t("competition.openOptions", "Abrir opciones")}
                  sx={{ color: POSITION_STYLES[1].color }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box
                position="relative"
                zIndex={1}
                my={0.8}
                minWidth={0}
                textAlign="center"
              >
                <Typography fontWeight={900} fontSize={15} lineHeight={1.15}>
                  {getName(first)}
                </Typography>
                <Typography
                  fontSize={11.5}
                  fontWeight={650}
                  sx={{ opacity: 0.72 }}
                  noWrap
                >
                  {first.author || "-"}
                </Typography>
              </Box>

              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  gap: 0.8,
                  width: "100%",
                }}
              >
                <ProfitRiskBox
                  profit={metricNumber(first.profitPct)}
                  risk={metricNumber(first.riskPct)}
                  profitLabel="Profit"
                  profitDecimals={2}
                  riskDecimals={2}
                  variant="body2"
                  px={0.8}
                  py={0.2}
                />
                <ScoreBox
                  profit={metricNumber(first.profitPct)}
                  risk={metricNumber(first.riskPct)}
                  score={metricNumber(first.score)}
                  scoreDecimals={2}
                  variant="body2"
                  px={0.8}
                  py={0.2}
                />
              </Box>
            </Box>

            {remaining.length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${remaining.length}, minmax(0, 1fr))`,
                  gap: 0.8,
                  minHeight: 0,
                }}
              >
                {remaining.map((item) => {
                  const position = Number(item.position);
                  const style = POSITION_STYLES[position] || POSITION_STYLES[3];

                  return (
                    <Box
                      key={`${item.kind}-${item.id}-${position}`}
                      sx={{
                        position: "relative",
                        minWidth: 0,
                        p: 1,
                        borderRadius: "11px",
                        color: style.color,
                        background: style.background,
                        border: `1px solid ${style.border}`,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        gap={0.4}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={0.6}
                          minWidth={0}
                        >
                          <Box
                            sx={{
                              width: 27,
                              height: 27,
                              borderRadius: "8px",
                              display: "grid",
                              placeItems: "center",
                              color: "#FFFFFF",
                              background: style.accent,
                              fontSize: 12,
                              fontWeight: 950,
                              flexShrink: 0,
                            }}
                          >
                            {position}
                          </Box>
                          <Typography fontWeight={950} fontSize={12} noWrap>
                            {PRIZES[position]} EUR
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(event) => onOpenMenu(event, item)}
                          aria-label={t(
                            "competition.openOptions",
                            "Abrir opciones"
                          )}
                          sx={{ p: 0.2, color: style.color }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box minWidth={0} my={0.55} textAlign="center">
                        <Typography fontWeight={850} fontSize={11.5} noWrap>
                          {getName(item)}
                        </Typography>
                        <Typography fontSize={10} sx={{ opacity: 0.72 }} noWrap>
                          {item.author || "-"}
                        </Typography>
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-end"
                        gap={0.45}
                        width="100%"
                      >
                        <ProfitRiskBox
                          profit={metricNumber(item.profitPct)}
                          risk={metricNumber(item.riskPct)}
                          profitLabel="Profit"
                          profitDecimals={2}
                          riskDecimals={2}
                          variant="body2"
                          px={0.65}
                          py={0.15}
                        />
                        <ScoreBox
                          profit={metricNumber(item.profitPct)}
                          risk={metricNumber(item.riskPct)}
                          score={metricNumber(item.score)}
                          scoreDecimals={2}
                          variant="body2"
                          px={0.65}
                          py={0.15}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        ) : (
          <Box flex={1} display="grid" sx={{ placeItems: "center" }}>
            <Typography color="#8A93A2" fontSize={12} textAlign="center">
              {t("competition.noParticipants", "Todavia no hay participantes")}
            </Typography>
          </Box>
        )}

      </Box>
    </Box>
  );
};

DashboardCompetitionCard.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      kind: PropTypes.string,
      id: PropTypes.number,
      position: PropTypes.number,
      title: PropTypes.string,
      robotName: PropTypes.string,
      strategyName: PropTypes.string,
      author: PropTypes.string,
      score: PropTypes.number,
      profitPct: PropTypes.number,
      riskPct: PropTypes.number,
    })
  ).isRequired,
  lastUpdated: PropTypes.instanceOf(Date),
  onOpenMenu: PropTypes.func.isRequired,
  infoUrl: PropTypes.string.isRequired,
  joinUrl: PropTypes.string.isRequired,
};

export default DashboardCompetitionCard;
